
import React, {
    useEffect,
    useState,
    useContext,
    useCallback,
} from "react";

import SingleItem from "./SingleItem";
import { toast } from "react-toastify";
import axios from "axios";
import { DatePicker } from "antd";
import CustomPagination from "../../../CustomPagination/CustomPagination";
import { RotatingSquare } from "react-loader-spinner";
import { AuthContext } from "../../../../Contexts/AuthContext/AuthProvider";

const { RangePicker } = DatePicker;

const API_URL = "https://api.velormall.com/api/agent/transaction-logs/view";

const AgentTransactionLogs = () => {
    const { authUser } = useContext(AuthContext);

    const [loading, setLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);

    const [data, setData] = useState([]);
    const [paginateData, setPaginateData] = useState({
        page: 1,
        limit: 10,
        length: 0,
        pageCount: 0,
    });

    // Filter states
    const [trx_typeFilter, setTrx_typeFilter] = useState("");
    const [nameFilter, setNameFilter] = useState("");
    const [Remark, setRemark] = useState("");
    const [dateValue, setDateValue] = useState([]);
    const [dateValueShow, setDateValueShow] = useState([]);

    // Keep the applied filters for pagination
    const [appliedFilters, setAppliedFilters] = useState({});

    // Fetch transactions
    const fetchTransactions = useCallback(
        async (page = 1, filters = {}) => {
            if (!authUser?._id) {
                setData([]);
                setPaginateData({
                    page: 1,
                    limit: 10,
                    length: 0,
                    pageCount: 0,
                });
                setLoading(false);
                setFilterLoading(false);
                return;
            }

            try {
                const params = {
                    page,
                    limit: 10,
                    ...filters,
                };

                // Remove empty filters
                Object.keys(params).forEach((key) => {
                    if (
                        params[key] === "" ||
                        params[key] === null ||
                        params[key] === undefined
                    ) {
                        delete params[key];
                    }
                });

                const response = await axios.get(
                    `${API_URL}/${authUser._id}`,
                    { params }
                );

                if (response.data?.success) {
                    setData(response.data.data || []);
                    setPaginateData(response.data);
                } else {
                    setData([]);
                    setPaginateData({
                        page,
                        limit: 10,
                        length: 0,
                        pageCount: 0,
                    });
                }
            } catch (error) {
                console.error("Agent transaction error:", error);

                toast.error(
                    error.response?.data?.message ||
                        "Failed to load transaction logs"
                );

                setData([]);
            } finally {
                setLoading(false);
                setFilterLoading(false);
            }
        },
        [authUser?._id]
    );

    // Initial load: fetch all transactions without filters
    useEffect(() => {
        fetchTransactions(1, {});
    }, [fetchTransactions]);

    // Pagination: preserve applied filters
    const handlePage = (event) => {
        const pageNumber = (event?.selected ?? 0) + 1;

        fetchTransactions(pageNumber, appliedFilters);
    };

    // Apply filters
    const handleSubmitData = async (event) => {
        event.preventDefault();

        const filters = {
            search: nameFilter.trim(),
            trx_type: trx_typeFilter,
            remark: Remark,
            start_date: dateValue[0] || "",
            end_date: dateValue[1] || "",
        };

        setAppliedFilters(filters);
        setFilterLoading(true);

        await fetchTransactions(1, filters);

        // Success/error feedback is handled by fetchTransactions.
    };

    // Reset filters and fetch all transactions again
    const handleReset = () => {
        setNameFilter("");
        setTrx_typeFilter("");
        setRemark("");
        setDateValue([]);
        setDateValueShow([]);
        setAppliedFilters({});
        setFilterLoading(true);

        fetchTransactions(1, {});
    };

    // Date range selection
    const handleDateChange = (dates) => {
        if (!dates) {
            setDateValue([]);
            setDateValueShow([]);
            return;
        }

        setDateValueShow(dates);

        // Send YYYY-MM-DD to the backend
        setDateValue(
            dates.map((date) => date.format("YYYY-MM-DD"))
        );
    };

    if (loading) {
        return (
            <div className="loader-css">
                <RotatingSquare
                    visible={true}
                    height="100"
                    width="100"
                    color="#4fa94d"
                    ariaLabel="rotating-square-loading"
                    wrapperStyle={{}}
                    wrapperClass=""
                />
            </div>
        );
    }

    return (
        <div className="bodywrapper__inner">
            {/* Page heading */}
            <div className="d-flex mb-30 flex-wrap gap-3 justify-content-between align-items-center">
                <h6 className="page-title">Transaction Logs</h6>
            </div>

            <div className="row">
                <div className="col-lg-12">
                    {/* Filter section */}
                    <div className="card responsive-filter-card mb-4">
                        <div className="card-body">
                            <form onSubmit={handleSubmitData}>
                                <div className="d-flex flex-wrap gap-4">
                                    {/* TRX search */}
                                    <div className="flex-grow-1">
                                        <label htmlFor="search">
                                            TRX
                                        </label>

                                        <input
                                            type="text"
                                            name="search"
                                            id="search"
                                            className="form-control"
                                            placeholder="Search transaction"
                                            value={nameFilter}
                                            onChange={(e) =>
                                                setNameFilter(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                    {/* Transaction type */}
                                    <div className="flex-grow-1">
                                        <label htmlFor="trx_type">
                                            Type
                                        </label>

                                        <select
                                            name="trx_type"
                                            id="trx_type"
                                            className="form-control"
                                            value={trx_typeFilter}
                                            onChange={(e) =>
                                                setTrx_typeFilter(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="">All</option>
                                            <option value="plus">
                                                Plus
                                            </option>
                                            <option value="minus">
                                                Minus
                                            </option>
                                        </select>
                                    </div>

                                    {/* Remark */}
                                    <div className="flex-grow-1">
                                        <label htmlFor="remark">
                                            Remark
                                        </label>

                                        <select
                                            id="remark"
                                            name="remark"
                                            className="form-control"
                                            value={Remark}
                                            onChange={(e) =>
                                                setRemark(e.target.value)
                                            }
                                        >
                                            <option value="">Any</option>

                                            <option value="balance_add">
                                                Balance add
                                            </option>

                                            <option value="balance_subtract">
                                                Balance subtract
                                            </option>

                                            <option value="deposit">
                                                Deposit
                                            </option>

                                            <option value="withdraw">
                                                Withdraw
                                            </option>

                                            <option value="withdraw_reject">
                                                Withdraw reject
                                            </option>

                                            <option value="order">
                                                Order
                                            </option>
                                        </select>
                                    </div>

                                    {/* Date range */}
                                    <div className="flex-grow-1">
                                        <label>Date</label>

                                        <RangePicker
                                            className="w-100"
                                            value={dateValueShow}
                                            format="YYYY-MM-DD"
                                            onChange={handleDateChange}
                                        />
                                    </div>

                                    {/* Filter button */}
                                    <div className="flex-grow-1 align-self-end">
                                        <button
                                            type="submit"
                                            className="btn btn-primary w-100 h-45"
                                            disabled={filterLoading}
                                        >
                                            {filterLoading
                                                ? "Filtering..."
                                                : "Filter"}
                                        </button>
                                    </div>

                                    {/* Reset button */}
                                    <div className="flex-grow-1 align-self-end">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary w-100 h-45"
                                            onClick={handleReset}
                                            disabled={filterLoading}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Transaction table */}
                    <div className="card b-radius--10">
                        <div className="card-body p-0">
                            <div className="table-responsive--sm table-responsive">
                                <table className="table table--light style--two">
                                    <thead>
                                        <tr>
                                            <th>User Name / ID</th>
                                            <th>TRX</th>
                                            <th>Transacted</th>
                                            <th>Amount</th>
                                            <th>Post Balance</th>
                                            <th>Details</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {data.length > 0 ? (
                                            data.map((item, index) => (
                                                <SingleItem
                                                    data={item}
                                                    index={index}
                                                    key={item._id}
                                                    paginateData={paginateData}
                                                />
                                            ))
                                        ) : (
                                            <tr>
                                                <td
                                                    className="text-muted text-center"
                                                    colSpan={6}
                                                >
                                                    Data not found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Pagination */}
                        {paginateData.pageCount > 1 && (
                            <CustomPagination
                                data={paginateData}
                                handlePage={handlePage}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgentTransactionLogs;
